/**
 * Frontend mirror of backend/seo_derive.py — derive SEO title, meta description,
 * keywords, hero alt and Product JSON-LD from the REAL product fields on the
 * storefront's anabiya.js catalogue. Admin overrides fetched from the backend
 * take precedence per-field. No fabricated claims (Organic / OEKO-TEX /
 * Egyptian / handmade / origin / certifications) — the derivation only reads
 * from the product data that already exists.
 */

export const BRAND = "Anabiya Decor";
const SITE_URL = "https://anabiyadecor.com";

const KEYWORD_RULES = [
  [/\bsateen\b/i, "cotton sateen bedding"],
  [/\bpercale\b/i, "percale cotton bedding"],
  [/\blinen\b/i, "linen bedding"],
  [/\bsilk\b/i, "silk bedding"],
  [/\bvelvet\b/i, "velvet home decor"],
  [/\bcashmere\b/i, "cashmere throw"],
  [/\bcotton\b/i, "premium cotton bedding"],
  [/\b\d{3,4}\s*tc\b/i, "high thread count bedding"],
  [/\bscallop/i, "scalloped bedding"],
  [/\bhand[- ]?embroider|chain[- ]?stitch/i, "embroidered bedding"],
  [/\bembroider/i, "embroidered bedding"],
  [/\bhand[- ]?block|block[- ]?print/i, "block print bedding"],
  [/\bquilt/i, "quilted bedding"],
  [/\bpleat/i, "pleated bedding"],
  [/\bmonogram|initial|bespoke|personalis/i, "monogrammed bedding"],
  [/\bpiped|piping/i, "piped bedding"],
];

const CATEGORY_PHRASES = [
  [/\bsateen\b/i, "cotton sateen"],
  [/\bpercale\b/i, "percale weave"],
  [/\blinen\b/i, "pure linen"],
  [/\bsilk\b/i, "silk"],
  [/\bvelvet\b/i, "velvet"],
  [/\bcashmere\b/i, "cashmere blend"],
  [/\bcotton\b/i, "premium cotton"],
  [/\b\d{3,4}\s*tc\b/i, "high thread count"],
  [/\bscallop/i, "scalloped trim"],
  [/\bhand[- ]?embroider|chain[- ]?stitch/i, "hand embroidered"],
  [/\bembroider/i, "embroidered detail"],
  [/\bhand[- ]?block|block[- ]?print/i, "hand block printed"],
  [/\bquilt/i, "quilted"],
  [/\bpleat/i, "pleated finish"],
  [/\bmonogram|initial|bespoke|personalis/i, "custom monogram"],
  [/\bpiped|piping/i, "piped edge"],
];

const CATEGORY_KEYWORDS = {
  "duvet-covers": "luxury duvet cover",
  "duvet-sets": "luxury duvet set",
  "sheet-sets": "premium sheet set",
  "pillow-shams": "matching pillow sham",
  "cushions": "designer cushion cover",
  "throws": "luxury throw",
  "throw-blankets": "luxury throw blanket",
  "curtains": "custom curtains",
  "bed-skirts": "bed skirt",
  "bed-runners": "bed runner",
  "headboards": "upholstered headboard",
  "monograms": "monogrammed bedding",
  "table-linens": "linen table runner",
};

const clean = (t) => String(t || "").replace(/\s+/g, " ").trim();
const truncate = (t, max) => {
  const s = clean(t);
  if (s.length <= max) return s;
  return s.slice(0, max - 1).replace(/\s+\S*$/, "").replace(/[,;:.\- ]+$/, "") + "…";
};
const firstSentence = (text, max = 200) => {
  const s = clean(text);
  if (!s) return "";
  const parts = s.split(/(?<=[.!?])\s+/);
  let out = parts[0];
  for (let i = 1; i < parts.length; i++) {
    if (out.length + parts[i].length + 1 > max) break;
    out = `${out} ${parts[i]}`;
  }
  return out;
};

const deriveSeoTitle = (p) => {
  const title = clean(p.title);
  if (!title) return BRAND;
  const material = clean(p.material);
  let cue = "";
  if (material) {
    const m = material.match(/(\d{3,4}\s*tc[^,]*)/i);
    cue = m ? m[1] : material.split(",")[0].trim();
    cue = truncate(cue, 22);
  }
  const opts = [
    cue && !title.toLowerCase().includes(cue.toLowerCase()) ? `${title} · ${cue} · ${BRAND}` : null,
    `${title} · ${BRAND}`,
    title,
  ];
  for (const o of opts) if (o && o.length <= 60) return o;
  return truncate(`${title} · ${BRAND}`, 60);
};

const deriveMetaDescription = (p) => {
  const title = clean(p.title);
  const desc = firstSentence(p.description || p.caption || "", 200);
  const parts = [];
  if (title) parts.push(title.endsWith(".") ? title : title + ".");
  if (desc) parts.push(/[.!?…]$/.test(desc) ? desc : desc + ".");
  const material = clean(p.material);
  const already = parts.join(" ").toLowerCase();
  if (material && !already.includes(material.toLowerCase())) parts.push(material + ".");
  const sizes = [...(p.sizes || []), ...(p.size_inches || [])].filter(Boolean);
  if (sizes.length && !already.includes("size")) parts.push(`Sizes: ${sizes.slice(0, 4).join(", ")}.`);
  parts.push("Shipped worldwide.");
  return truncate(parts.join(" "), 160);
};

const deriveKeywords = (p) => {
  const text = ["title", "description", "caption", "material", "care", "category"]
    .map((k) => p[k] || "")
    .join(" ");
  const out = [];
  const cat = (p.category || "").toLowerCase();
  if (CATEGORY_KEYWORDS[cat]) out.push(CATEGORY_KEYWORDS[cat]);
  for (const [pat, kw] of KEYWORD_RULES) if (pat.test(text) && !out.includes(kw)) out.push(kw);
  for (const c of p.colours || []) {
    const name = typeof c === "string" ? c : c?.name;
    if (name && out.length < 8) {
      const kw = cat ? `${name.toLowerCase()} bedding` : name.toLowerCase();
      if (!out.includes(kw)) out.push(kw);
    }
  }
  const seen = new Set();
  return out.filter((k) => (seen.has(k.toLowerCase()) ? false : seen.add(k.toLowerCase())) && true).slice(0, 8);
};

const deriveAltHero = (p) => {
  const title = clean(p.title);
  const material = clean((p.material || "").split(/[,.]/)[0]);
  const colours = (p.colours || [])
    .map((c) => (typeof c === "string" ? c : c?.name))
    .filter(Boolean);
  const bits = [title];
  if (colours[0] && !title.toLowerCase().includes(colours[0].toLowerCase())) bits.push(`in ${colours[0]}`);
  if (material && !title.toLowerCase().includes(material.toLowerCase())) bits.push(`— ${material}`);
  return truncate(bits.join(" "), 125);
};

const deriveAltAlternate = (p, i) => {
  const base = clean(p.title);
  if (i === 0) return deriveAltHero(p);
  const labels = ["detail view", "close-up", "styled bedroom scene", "back view", "fabric texture"];
  return truncate(`${base} — ${labels[(i - 1) % labels.length]}`, 125);
};

/** Public helper: best available ALT text for a product image (real fields). */
export const productAltText = (p) => (p ? deriveAltHero(p) || clean(p.title) : "");

/** Category-page SEO derived from the real products in that category. */
export const deriveCategorySeo = (catSlug, catTitle, sampleProducts = []) => {
  const human = clean(catTitle) || clean(catSlug).replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const hits = {};
  for (const p of sampleProducts || []) {
    const text = [p?.title, p?.material, p?.description].filter(Boolean).join(" ");
    for (const [pat, phrase] of CATEGORY_PHRASES) {
      if (pat.test(text)) hits[phrase] = (hits[phrase] || 0) + 1;
    }
  }
  const top = Object.entries(hits).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([phrase]) => phrase);
  const kw = CATEGORY_KEYWORDS[catSlug] || "home textiles";
  const seoTitle = truncate(`${human} · Luxury ${kw} · ${BRAND}`, 60);
  const n = (sampleProducts || []).length;
  const parts = [`${human} from ${BRAND}.`];
  if (n) parts.push(`Browse ${n} design${n === 1 ? "" : "s"}.`);
  if (top.length) parts.push(`Featuring ${top.join(", ")}.`);
  parts.push("Shipped worldwide.");
  const intro = parts.join(" ");
  const metaDescription = truncate(intro, 160);
  return {
    seo_title: seoTitle,
    meta_title: seoTitle,
    meta_description: metaDescription,
    seo_description: metaDescription,
    h1: human,
    intro,
    canonical_url: `${SITE_URL}/shop?category=${catSlug}`,
    og_type: "website",
  };
};

/** Full SEO record derived from the product's real fields only. */
export const deriveProductSeo = (product) => {
  if (!product) return null;
  const slug = product.slug || "";
  const seoTitle = deriveSeoTitle(product);
  const metaDescription = deriveMetaDescription(product);
  const altHero = deriveAltHero(product);
  const keywords = deriveKeywords(product);
  const images = product.images || [];
  const altAlternates = images.map((_, i) => deriveAltAlternate(product, i));
  return {
    seo_title: seoTitle,
    meta_title: seoTitle,
    meta_description: metaDescription,
    seo_description: metaDescription,
    seo_keywords: keywords,
    seo_alt_hero: altHero,
    alt_alternates: altAlternates,
    canonical_url: slug ? `${SITE_URL}/product/${slug}` : SITE_URL,
    og_type: "product",
    brand: BRAND,
    auto_generated: true,
  };
};

/**
 * Resolve SEO for the storefront: `overrides` (from `/api/products/{slug}/seo`)
 * wins per-field, otherwise the derived value is used.
 */
export const resolveProductSeo = (product, overrides = null) => {
  const derived = deriveProductSeo(product);
  if (!derived) return null;
  const out = { ...derived, _overridden: [] };
  if (overrides) {
    for (const k of ["seo_title", "seo_description", "seo_alt_hero"]) {
      const v = overrides[k];
      if (typeof v === "string" && v.trim()) {
        out[k] = v.trim();
        out._overridden.push(k);
      }
    }
    const kw = overrides.seo_keywords;
    if (Array.isArray(kw) && kw.length) {
      out.seo_keywords = kw.map(String).map((x) => x.trim()).filter(Boolean);
      out._overridden.push("seo_keywords");
    }
  }
  out.meta_title = out.seo_title;
  out.meta_description = out.seo_description;
  out.auto_generated = out._overridden.length === 0;
  return out;
};

/** Product JSON-LD. Never adds ratings/reviews. */
export const buildProductJsonLd = (product, resolved, priceUsd = null) => {
  if (!product) return null;
  const slug = product.slug || "";
  const canonical = resolved?.canonical_url || `${SITE_URL}/product/${slug}`;
  const images = (product.images || []).map((u) =>
    typeof u === "string" && u.startsWith("http") ? u : `${SITE_URL}${u}`
  );
  const stock = product.stock;
  const inStock = stock == null || Number(stock) > 0;
  const availability = inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
  const sizePrices = product.size_prices || {};
  let offers;
  if (sizePrices && Object.keys(sizePrices).length) {
    offers = Object.entries(sizePrices)
      .filter(([, p]) => Number(p) > 0)
      .map(([name, p]) => ({
        "@type": "Offer",
        sku: `${slug}-${String(name).toLowerCase().replace(/\s+/g, "-")}`,
        name,
        price: Number(p).toFixed(2),
        priceCurrency: "USD",
        availability,
        url: canonical,
      }));
  } else {
    const p = priceUsd != null ? Number(priceUsd) : Number(product.price || 0);
    offers = {
      "@type": "Offer",
      price: p.toFixed(2),
      priceCurrency: "USD",
      availability,
      url: canonical,
    };
  }
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title || slug,
    description: resolved?.meta_description || "",
    image: images,
    sku: slug,
    brand: { "@type": "Brand", name: BRAND },
    url: canonical,
    offers,
  };
};
